import Email16 from '@carbon/icons-react/es/email/16';
import Hourglass16 from '@carbon/icons-react/es/hourglass/16';
import UserAdmin16 from '@carbon/icons-react/es/user--admin/16';
import { Col, Row, Space } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Tag } from 'components/atoms/tag';
import { TeamMemberSectionField } from 'components/organisms/module-settings/team-members/section-field/section-field';
import { ProductiveHeading05 } from 'components/styles';
import { Member } from 'data/members/api';
import { useFullName } from 'hooks/use-idb';
import { TeamMemberDrawerSectionProps } from '../types';

const RoleAndPermission = styled.div`
  color: var(--gray-50) !important;
  font-weight: var(--heading-4-weight);
  margin-bottom: var(--spacing-05);
`;

const Container = styled.div`
  margin-bottom: var(--spacing-07);
`;

const StyledRole = styled.span`
  margin-right: var(--spacing-05);
`;

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSpan = styled.span`
  margin-left: var(--spacing-03);
`;

const SubTextSpan = styled.span`
  margin-left: var(--spacing-03);
  color: var(--gray-50);
`;

const GeneralInfo = styled.div`
  padding-top: var(--spacing-05);
  height: 100%;
  .username {
    ${ProductiveHeading05};
    margin-bottom: 0;
  }
  .ozone-select__multi-value__remove {
    display: none;
  }
`;

const Edit = styled(Col)`
  display: flex;
  justify-content: end;
`;

export const TeamMemberViewModal: FC<{
  member: Member;
  changeToEditMode(member: Member);
  sectionProps: TeamMemberDrawerSectionProps;
}> = ({ member, changeToEditMode, sectionProps }) => {
  const username = `${member?.user?.firstName ?? ''} ${
    member?.user?.lastName ?? ''
  }`;
  const approverName = useFullName(member.user.timeSheetApprover);

  const customFields = member?.user?.fields;

  return (
    <GeneralInfo>
      <Container>
        <Row align="middle">
          <Col span={18}>
            <div className="username">{username}</div>
          </Col>
          <Edit span={6}>
            <Button
              data-cy="settings.team_members.edit_profile"
              type="link"
              size="small"
              onClick={() => changeToEditMode(member)}
            >
              <FormattedMessage id="settings.team_members.edit_profile" />
            </Button>
          </Edit>
        </Row>
        {(member?.user?.role || member?.permissionSet) && (
          <RoleAndPermission>
            {member?.user?.role?.roleName && (
              <StyledRole>{member.user.role.roleName}</StyledRole>
            )}
            {member?.permissionSet && (
              <Tag color="blue">{member.permissionSet.permissionSetName}</Tag>
            )}
          </RoleAndPermission>
        )}

        <Space direction="vertical">
          <Row align="middle">
            <StyledDiv>
              <Email16 />
              <StyledSpan>{member?.user?.emailId}</StyledSpan>
            </StyledDiv>
          </Row>
          {sectionProps.hideTimesheetApproverSection === true ? null : (
            <Row>
              <StyledDiv>
                <UserAdmin16 />
                <StyledSpan>
                  {approverName || (
                    <FormattedMessage id="settings.team_members.default.timesheet.approver" />
                  )}
                </StyledSpan>
                <SubTextSpan>
                  (
                  <FormattedMessage id="settings.team_members.timesheet.approver" />
                  )
                </SubTextSpan>
              </StyledDiv>
            </Row>
          )}
          {sectionProps.hideCapacitySection === true ? null : (
            <Row>
              <StyledDiv>
                <Hourglass16 />
                <StyledSpan>
                  <FormattedMessage
                    id="settings.team_members.capacity.view.capacity"
                    values={{ hours: member.user.capacity }}
                  />
                </StyledSpan>
              </StyledDiv>
            </Row>
          )}
        </Space>
      </Container>

      {sectionProps.hideFieldsSection === true ? null : (
        <TeamMemberSectionField readOnly fields={customFields} />
      )}
    </GeneralInfo>
  );
};
